import {
  Row,
  Col,
  Typography,
  Button,
  Avatar,
  Spin,
  Card,
  Tooltip,
} from "antd";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useLoaderData, useNavigate, useNavigation } from "react-router-dom";
import "./Home.scss";
import {
  CrownOutlined,
  InstagramOutlined,
  TikTokOutlined,
} from "@ant-design/icons";
import { ReactComponent as LocationIcon } from "assets/images/location-icon.svg";
import { ReactComponent as ClockIcon } from "assets/images/clock-icon.svg";
import { ReactComponent as TicketIcon } from "assets/images/ticket-icon.svg";
import { ReactComponent as MessagesIcon } from "assets/images/message-text-icon.svg";
import { ReactComponent as FlashIcon } from "assets/images/flash-icon.svg";
import { ReactComponent as StarIcon } from "assets/images/star-icon.svg";
import { ReactComponent as PurchaseIcon } from "assets/images/profile-icon.svg";
import { ReactComponent as ChevronRightIcon } from "assets/images/chevron-right-icon.svg";
import OpenCollabs from "assets/images/create-collab-success.png";
import ClosedCollabs from "assets/images/create-collab-onboard.png";
import { Collab } from "interfaces/collab";
import dayjs from "dayjs";
import InfluencerModal from "components/influencer-modal";
import { formatNumberKMBT } from "pages/influencers/InfluencerCard";
import { isMobile } from "react-device-detect";
import EmptyHomeCard from "pages/onboarding/EmptyHomeCard";
import { CollabsState } from "redux-context/collabs";
import { BusinessState } from "redux-context/business";
import { SubscriptionType } from "enums/SubscriptionType";
import CreateCollabOonboard from "assets/images/create-collab-onboard.png";
import { collabStartChat } from "services/SauceService";
import RescheduleRequest from "components/reschedule-request/RescheduleRequest";
import { BusinessCampaignCollab } from "enums/BusinessCampaingCollab";
import { motion } from "motion/react";
import CustomSelect from "components/custom-select/CustomSelect";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLocationDot } from "@fortawesome/free-solid-svg-icons";
import { Venue } from "interfaces/venue";

const monthNames = [
  "january",
  "february",
  "march",
  "april",
  "may",
  "june",
  "july",
  "august",
  "september",
  "october",
  "november",
  "december",
];

enum ApplicationStatus {
  UPCOMING = "Upcoming",
  COMPLETED = "Completed",
}

const filterByVenueAndStatus = (
  list: Collab[] = [],
  selectedStatus: string,
  selectedLocations?: string | string[]
) => {
  const selected = Array.isArray(selectedLocations)
    ? selectedLocations.filter(Boolean)
    : selectedLocations
    ? [selectedLocations]
    : [];

  const isAllLocations = selected.length === 0 || selected.includes("all");

  return list.filter((collab: Collab) => {
    const isUpcoming = selectedStatus === ApplicationStatus.UPCOMING;

    if (!isAllLocations && !selected.includes(collab?.venue?.uuid))
      return false;

    return isUpcoming
      ? collab?.tabStatus?.toLowerCase() === "upcoming"
      : collab?.tabStatus?.toLowerCase() === "completed";
  });
};

const EmptySelectedTab = () => {
  const navigate = useNavigate();

  const onClickCreateCollab = () => {
    navigate("/collabs/new");
  };

  const onClickCreateCampaign = () => {
    navigate("/campaign/new");
  };

  const { numbers } = useSelector((state: CollabsState) => state.collabs);
  const { business } = useSelector((state: BusinessState) => state.business);

  const OpenCollabsContent = () => {
    return (
      <>
        <Row justify="center" style={{ marginBottom: 32 }}>
          <img src={OpenCollabs} alt="Collabs empty" />
        </Row>
        <Typography.Text className="text-regular">
          Influencer applications, arrival times, and scheduled visits will
          appear here as they come in.
        </Typography.Text>
      </>
    );
  };

  const ClosedCollabsContent = () => {
    return (
      <>
        <Row justify="center" style={{ marginBottom: 32 }}>
          <img src={ClosedCollabs} alt="Collabs empty" />
        </Row>
        <Typography.Text className="text-regular">
          Your collabs are completed! But the momentum doesn't stop here...
          Start a new one!
        </Typography.Text>
        <Row justify="center" style={{ marginTop: 20 }}>
          <Button type="primary" onClick={onClickCreateCollab}>
            Create Collab
          </Button>
        </Row>
      </>
    );
  };

  const ClosedCampaignsContent = () => {
    return (
      <>
        <Row justify="center" style={{ marginBottom: 32 }}>
          <img src={ClosedCollabs} alt="Collabs empty" />
        </Row>
        <Typography.Text className="text-regular">
          Your campaigns are completed! But the momentum doesn't stop here...
          Start a new one!
        </Typography.Text>
        <Row justify="center" style={{ marginTop: 20 }}>
          <Button type="primary" onClick={onClickCreateCampaign}>
            Create Campaign
          </Button>
        </Row>
      </>
    );
  };

  return (
    <div>
      <Card>
        <Row justify="center">
          <Col md={12} xs={24} style={{ textAlign: "center" }}>
            {(numbers.totalOpenCollabs || 0) > 0 ? (
              <OpenCollabsContent />
            ) : business.viewMode === BusinessCampaignCollab.CampaignOnly ||
              business.viewMode === BusinessCampaignCollab.CollabAndCampaign ? (
              <ClosedCampaignsContent />
            ) : (
              <ClosedCollabsContent />
            )}
          </Col>
        </Row>
      </Card>
    </div>
  );
};

const Today = ({
  collab,
  onClickFoodie,
}: {
  collab: any;
  onClickFoodie: (foodieId: string) => void;
}) => {
  const navigate = useNavigate();
  const onClickMessage = async (collab: any) => {
    const { collabId, bookingId } = collab;
    const response: any = await collabStartChat({ collabId, bookingId });
    if (response?.chat?._id) navigate(`/messenger/${response.chat._id}`);
  };
  return (
    <Col md={24} key={collab.bookingId}>
      <div className="today">
        <div className="application">
          <div
            style={{
              display: "flex",
              gap: 16,
              alignItems: "center",
              flex: isMobile ? "none" : 3,
            }}
          >
            <div
              style={{ position: "relative", cursor: "pointer" }}
              onClick={() => onClickFoodie(collab.foodieId)}
            >
              <Avatar src={collab.avatarUrl} size={60} />
              {collab.isPremium && (
                <span className="diamond">
                  <CrownOutlined />
                </span>
              )}
            </div>
            <div>
              <span className="name">{collab?.username}</span>
              <span style={{ marginRight: 8 }}>
                <InstagramOutlined style={{ marginRight: 4 }} />
                {formatNumberKMBT(collab?.instagramFollowersCount)}
              </span>
              <span>
                <TikTokOutlined style={{ marginRight: 4 }} />
                {formatNumberKMBT(collab?.tiktokFollowersCount)}
              </span>
            </div>
          </div>
          <div className="box-icon" style={{ flex: isMobile ? "none" : 3 }}>
            <LocationIcon />
            <div>
              <span className="name ellipsis" style={{ width: 270 }}>
                {collab?.venue?.name}
              </span>
              <span className="value ellipsis" style={{ width: 270 }}>
                {collab?.venue.addressDisplay || collab?.venue?.address}
              </span>
            </div>
          </div>
          <div className="box-icon" style={{ flex: isMobile ? "none" : 1 }}>
            <ClockIcon />
            <div>
              <span className="name">{collab?.applicationDate?.hour}</span>
              <span className="value">Today</span>
            </div>
          </div>
          <div className="box-icon" style={{ flex: isMobile ? "none" : 2 }}>
            <TicketIcon />
            <div>
              <span className="name">
                {collab?.securityCode}{" "}
                {collab?.deliveryAddress && ` ∘  ${collab?.deliveryAddress}`}
              </span>
              <span className="value">
                ${collab?.foodCompAmount}
                {" credit "}
                {collab?.guestAllowed && " ∘ 1 guest"}
              </span>
            </div>
          </div>
          <Button
            style={{
              display: isMobile ? "none" : "block",
            }}
            className="button-icon"
            icon={<MessagesIcon />}
            onClick={() => onClickMessage(collab)}
          >
            Chat
          </Button>
        </div>
        <div className="extra-info">
          <div
            style={{
              flex: 1,
              display: "flex",
              gap: 16,
              justifyContent: isMobile ? "center" : "flex-start",
            }}
          >
            <span>
              <PurchaseIcon /> {!isMobile && "Purchase Intent: "}
              {collab?.dinnerIntentByPosts || "-"}
              /post
            </span>
            <span>
              <FlashIcon /> {!isMobile && "Engagement: "}{" "}
              {collab?.engagementRate || "-"}%
            </span>
            <span>
              <StarIcon /> {!isMobile && "Rating: "}{" "}
              {collab?.commitmentRate || "-"}%
            </span>
          </div>
        </div>
      </div>
    </Col>
  );
};

const Pending = ({ collab }: { collab: any }) => {
  const navigate = useNavigate();
  if (isMobile)
    return (
      <Col
        xs={24}
        key={`PENDINGS_${collab.bookingId}`}
        onClick={() => navigate(`/collabs/${collab.collabId}`)}
      >
        <div
          className="application pending"
          style={{ flexDirection: "column", alignItems: "flex-start" }}
        >
          <div
            style={{
              display: "flex",
              gap: 16,
              alignItems: "center",
            }}
          >
            <div>
              <span className="label-strong">
                {collab?.applications.length} Applications
              </span>
            </div>
            <div className="box-icon" style={{ justifyContent: "center" }}>
              <Avatar.Group>
                {collab?.applications.slice(0, 10).map((application: any) => (
                  <Avatar src={application} size={40} key={application} />
                ))}
              </Avatar.Group>
            </div>
          </div>
          <div className="box-icon" style={{ flex: 3 }}>
            <LocationIcon className="svg-16" />
            <div>
              <span className="name">{collab?.venue?.name}</span>
              <span className="value">
                {collab?.venue.addressDisplay || collab?.venue?.address}
              </span>
            </div>
          </div>
        </div>
      </Col>
    );

  return (
    <Col xs={24} key={`PENDINGS_${collab.bookingId}`}>
      <div className="application pending">
        <div
          style={{
            display: "flex",
            gap: 16,
            alignItems: "center",
            flex: 3,
          }}
        >
          <span className="label-strong">
            {collab?.applications.length} Applications
          </span>
        </div>
        <div className="box-icon" style={{ flex: 3 }}>
          <LocationIcon className="svg-16" />
          <div>
            <span className="name">{collab?.venue?.name}</span>
            <span className="value">
              {collab?.venue.addressDisplay || collab?.venue?.address}
            </span>
          </div>
        </div>
        <div className="box-icon" style={{ flex: 2, justifyContent: "center" }}>
          <Avatar.Group>
            {collab?.applications.slice(0, 10).map((application: any) => (
              <Avatar src={application} size={40} key={application} />
            ))}
          </Avatar.Group>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            height: "100%",
            flex: 1,
          }}
        >
          <motion.div
            whileHover={{
              x: [0, 10, 0],
              transition: { duration: 1 },
            }}
          >
            <ChevronRightIcon
              style={{ fontSize: 24 }}
              onClick={() => navigate(`/collabs/${collab.collabId}`)}
            />
          </motion.div>
        </div>
      </div>
    </Col>
  );
};

const Home: React.FC = () => {
  const { today, ...years } = useLoaderData() as any;
  const navigate = useNavigate();
  const navigation = useNavigation();
  const loading = navigation.state === "loading";
  const [selectedStatus, setSelectedStatus] = useState<string>(
    ApplicationStatus.UPCOMING
  );

  // Allow selecting multiple locations
  const [selectedLocation, setSelectedLocation] = useState<string[]>([]);
  const [selectedFoodie, setSelectedFoodie] = useState<string>("");
  const [openFoodieModal, setOpenFoodieModal] = useState<boolean>(false);
  const { business, subscription } = useSelector(
    (state: BusinessState) => state.business
  );
  const { numbers } = useSelector((state: CollabsState) => state.collabs);
  const aiAutoAccept =
    subscription?.subscriptionType === SubscriptionType.ActiveWithAIAutoAccept;

  const orderedYears = Object.keys(years).sort((a, b) => +a - +b);

  const venueOptions = useMemo(() => {
    if (business) {
      return (
        business.venues?.map((venue: Venue) => ({
          value: venue.uuid,
          label: `${venue.name} - ${venue.addressDisplay}`,
        })) || []
      );
    }
  }, [business]);

  const hasItemList = useMemo(() => {
    return (
      today?.pendings?.length > 0 ||
      today?.approved?.length > 0 ||
      orderedYears.some((year) => {
        const currentYear = years[year];
        const orderedMonths = monthNames.filter((month) =>
          Object.keys(currentYear).includes(month)
        );
        return orderedMonths.some((month) => {
          const currentMonth = currentYear[month];
          return (
            filterByVenueAndStatus(
              currentMonth.pendings,
              selectedStatus,
              selectedLocation
            ).length > 0 ||
            filterByVenueAndStatus(
              currentMonth.approved,
              selectedStatus,
              selectedLocation
            ).length > 0
          );
        });
      })
    );
  }, [today, orderedYears, selectedStatus, selectedLocation, years]);

  const hasUpcoming = useMemo(() => {
    return (
      today?.pendings?.length > 0 ||
      today?.approved?.length > 0 ||
      orderedYears.some((year) => {
        const currentYear = years[year];
        const orderedMonths = monthNames.filter((month) =>
          Object.keys(currentYear).includes(month)
        );
        return orderedMonths.some((month) => {
          const currentMonth = currentYear[month];

          return (
            filterByVenueAndStatus(
              currentMonth.pendings,
              "Upcoming",
              selectedLocation
            ).length > 0 ||
            filterByVenueAndStatus(
              currentMonth.approved,
              "Upcoming",
              selectedLocation
            ).length > 0
          );
        });
      })
    );
  }, [today, orderedYears, selectedLocation, years]);

  const hasCompleted = useMemo(() => {
    return (
      today?.pendings?.length > 0 ||
      today?.approved?.length > 0 ||
      orderedYears.some((year) => {
        const currentYear = years[year];
        const orderedMonths = monthNames.filter((month) =>
          Object.keys(currentYear).includes(month)
        );
        return orderedMonths.some((month) => {
          const currentMonth = currentYear[month];

          return (
            filterByVenueAndStatus(
              currentMonth.pendings,
              "Completed",
              selectedLocation
            ).length > 0 ||
            filterByVenueAndStatus(
              currentMonth.approved,
              "Completed",
              selectedLocation
            ).length > 0
          );
        });
      })
    );
  }, [today, orderedYears, selectedLocation, years]);

  const onClickFoodie = (foodieId: string) => {
    setSelectedFoodie(foodieId);
    setOpenFoodieModal(true);
  };

  const todayFiltered = useMemo(() => {
    if (!today) return [];
    return filterByVenueAndStatus(
      today.approved,
      selectedStatus,
      selectedLocation
    );
  }, [today, selectedLocation, selectedStatus]);

  const showOnboarding = useMemo(() => {
    return numbers.reviewed === 0 && !aiAutoAccept;
  }, [numbers, aiAutoAccept]);

  const showWelcome = useMemo(() => {
    return numbers.totalCollabs === 0 && aiAutoAccept;
  }, [numbers, aiAutoAccept]);

  const onClickMessage = async (collab: any) => {
    const { collabId, bookingId } = collab;
    const response: any = await collabStartChat({ collabId, bookingId });
    if (response?.chat?._id) navigate(`/messenger/${response.chat._id}`);
  };

  useEffect(() => {
    if (hasUpcoming) {
      setSelectedStatus(ApplicationStatus.UPCOMING);
      return;
    }
    if (hasCompleted) {
      setSelectedStatus(ApplicationStatus.COMPLETED);
    }
  }, [hasUpcoming, hasCompleted]);

  if (loading) return <Spin />;

  if (showOnboarding)
    return (
      <div className="home-applications">
        <h1 className="header-1">Home</h1>
        <EmptyHomeCard />
      </div>
    );

  if (showWelcome) {
    return (
      <div className="home-applications">
        <h1 className="header-1">Welcome</h1>
        <div className="onboarding-card">
          <Card style={{ padding: 40, textAlign: "center" }}>
            <img src={CreateCollabOonboard} alt="Collabs empty" />
            <Typography.Title level={4}>Welcome!</Typography.Title>
            <Typography.Text style={{ display: "block" }}>
              We are glad to have you here. Please finish your onboarding!
            </Typography.Text>
            <Button
              type="primary"
              onClick={() => navigate("/welcome")}
              style={{ marginTop: 16 }}
            >
              Complete Onboarding
            </Button>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="home-applications">
      <InfluencerModal
        open={openFoodieModal}
        onCancel={() => setOpenFoodieModal(false)}
        foodieId={selectedFoodie}
      />
      <h1 className="header-1">Home</h1>
      <Row justify="space-between">
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: 24,
            marginBottom: 32,
          }}
        >
          <CustomSelect
            className="label-strong"
            value={selectedLocation}
            options={venueOptions}
            onChange={(value) => setSelectedLocation(value)}
            popupMatchSelectWidth={false}
            popupClassName="venue-select-popup"
            icon={<FontAwesomeIcon icon={faLocationDot} />}
            displayLabel="Location"
            mode="multiple"
            alwaysShowDisplayLabel
            showSelectedAsTags
          />
          <CustomSelect
            className="label-strong"
            value={selectedStatus}
            options={[
              {
                label: ApplicationStatus.UPCOMING,
                value: ApplicationStatus.UPCOMING,
              },
              {
                label: ApplicationStatus.COMPLETED,
                value: ApplicationStatus.COMPLETED,
              },
            ]}
            onChange={(value) => setSelectedStatus(value as string)}
            popupMatchSelectWidth={false}
          />
        </div>
      </Row>
      {/* <Row style={{ marginBottom: 40 }}>
        {(() => {
          const appliedItems: { key: string; label: string; value: any }[] = [];

          // Status filter
          if (selectedStatus) {
            appliedItems.push({
              key: "status",
              label: "Status",
              value: selectedStatus,
            });
          }

          // Location filter (multi-select)
          if (Array.isArray(selectedLocation) && selectedLocation.length > 0) {
            const locLabelsArr =
              (venueOptions || [])
                .filter((v: any) => selectedLocation.includes(v.value))
                .map((v: any) => v.label) || [];
            appliedItems.push({
              key: "location",
              label: "Location",
              value: locLabelsArr.length ? locLabelsArr : "Selected",
            });
          }

          if (appliedItems.length === 0) return null;

          return (
            <ul style={{ margin: 0 }}>
              {appliedItems.map((item) => (
                <li key={item.key} style={{ whiteSpace: "pre-line" }}>
                  <strong>{item.label}:</strong>{" "}
                  {Array.isArray(item.value)
                    ? item.value.map((val: string, idx: number) => (
                        <span key={idx}>
                          {val}
                          {idx !== item.value.length - 1 && <br />}
                        </span>
                      ))
                    : item.value}
                </li>
              ))}
            </ul>
          );
        })()}
      </Row> */}
      <Row gutter={[16, 16]}>
        {todayFiltered.length > 0 && (
          <>
            <Typography.Title level={3}>Today 🔥</Typography.Title>
            {todayFiltered.map((collab: any) => (
              <Today collab={collab} onClickFoodie={onClickFoodie} />
            ))}
          </>
        )}
        <Col md={24}>
          {hasItemList ? (
            orderedYears.map((year: any) => {
              if (!years[year]) return null;

              const currentYear = years[year];
              const orderedMonths = monthNames.filter((month) =>
                Object.keys(currentYear).includes(month)
              );
              const hasPendingOrApproved = orderedMonths.some((month: any) => {
                const currentMonth = currentYear[month];
                return (
                  filterByVenueAndStatus(
                    currentMonth.pendings,
                    selectedStatus,
                    selectedLocation
                  ).length > 0 ||
                  filterByVenueAndStatus(
                    currentMonth.approved,
                    selectedStatus,
                    selectedLocation
                  ).length > 0
                );
              });

              if (!hasPendingOrApproved) return null;

              return (
                <Row key={year}>
                  <Col xs={24}>
                    {orderedYears.length > 1 && (
                      <h3 className="header-2">{year}</h3>
                    )}
                    <Row gutter={[32, 32]} style={{ marginBottom: 16 }}>
                      <Col md={24}>
                        {orderedMonths.map((month: any) => {
                          if (!currentYear[month]) return null;
                          const currentMonthUnfiltered = currentYear[month];
                          const currentMonth = {
                            pendings: filterByVenueAndStatus(
                              currentMonthUnfiltered.pendings,
                              selectedStatus,
                              selectedLocation
                            ),
                            approved: filterByVenueAndStatus(
                              currentMonthUnfiltered.approved,
                              selectedStatus,
                              selectedLocation
                            ),
                          };

                          if (
                            currentMonth.pendings.length === 0 &&
                            currentMonth.approved.length === 0
                          )
                            return null;

                          return (
                            <Row key={month}>
                              <Col xs={24}>
                                <h3 className="header-2">
                                  {month.charAt(0).toUpperCase() +
                                    month.slice(1)}
                                </h3>
                                <Row
                                  gutter={[12, 12]}
                                  style={{ marginBottom: 16 }}
                                >
                                  {currentMonth.pendings.map((collab: any) => (
                                    <Pending collab={collab} />
                                  ))}
                                </Row>
                                <Row gutter={[32, 32]}>
                                  {currentMonth.approved.map((collab: any) => {
                                    const now = dayjs();
                                    const canRescheduleAccepted =
                                      selectedStatus ===
                                        ApplicationStatus.UPCOMING &&
                                      dayjs(
                                        collab?.applicationDate?.date
                                      ).isAfter(now.add(48, "hour"));
                                    return (
                                      <Col
                                        xs={24}
                                        key={`APPROVED_${collab.bookingId}`}
                                      >
                                        <div className="application">
                                          <div
                                            style={{
                                              display: "flex",
                                              gap: 16,
                                              alignItems: "center",
                                              flex: isMobile ? "none" : 3,
                                            }}
                                          >
                                            <div
                                              style={{
                                                position: "relative",
                                                cursor: "pointer",
                                              }}
                                              onClick={() =>
                                                onClickFoodie(collab.foodieId)
                                              }
                                            >
                                              <Avatar
                                                src={collab.avatarUrl}
                                                size={60}
                                              />
                                              {collab.isPremium && (
                                                <span className="diamond">
                                                  <CrownOutlined />
                                                </span>
                                              )}
                                            </div>
                                            <div>
                                              <span className="name">
                                                {collab?.username}
                                              </span>
                                              <span
                                                style={{ marginRight: 8 }}
                                                className="text-regular-small"
                                              >
                                                <InstagramOutlined
                                                  style={{ marginRight: 4 }}
                                                />
                                                {formatNumberKMBT(
                                                  collab?.instagramFollowersCount
                                                )}
                                              </span>
                                              <span className="text-regular-small">
                                                <TikTokOutlined
                                                  style={{ marginRight: 4 }}
                                                />
                                                {formatNumberKMBT(
                                                  collab?.tiktokFollowersCount
                                                )}
                                              </span>
                                            </div>
                                          </div>
                                          <div
                                            className="box-icon"
                                            style={{
                                              flex: isMobile ? "none" : 3,
                                            }}
                                          >
                                            <LocationIcon className="svg-16" />
                                            <div>
                                              <span
                                                className="name ellipsis"
                                                style={{ width: 270 }}
                                              >
                                                {collab?.venue?.name}
                                              </span>
                                              <span
                                                className="value ellipsis"
                                                style={{ width: 270 }}
                                              >
                                                {collab?.venue.addressDisplay ||
                                                  collab?.venue?.address}
                                              </span>
                                            </div>
                                          </div>
                                          <div
                                            className="box-icon"
                                            style={{
                                              flex: isMobile ? "none" : 1,
                                            }}
                                          >
                                            <ClockIcon />
                                            <div>
                                              <span className="name">
                                                {collab?.applicationDate?.hour}
                                              </span>
                                              <span className="value">
                                                {dayjs
                                                  .utc(
                                                    collab?.applicationDate
                                                      ?.date
                                                  )
                                                  .format("DD, MMMM, YYYY")}
                                              </span>
                                            </div>
                                          </div>
                                          <div
                                            className="box-icon"
                                            style={{
                                              flex: isMobile ? "none" : 2,
                                            }}
                                          >
                                            <TicketIcon />
                                            <div>
                                              <span className="name">
                                                {collab?.securityCode}{" "}
                                                {collab?.deliveryAddress &&
                                                  ` ∘  ${collab?.deliveryAddress}`}
                                              </span>
                                              <span className="value">
                                                ${collab?.foodCompAmount}
                                                {" credit "}
                                                {collab?.guestAllowed &&
                                                  " ∘ 1 guest"}
                                              </span>
                                            </div>
                                          </div>
                                          <div
                                            style={{
                                              display: "flex",
                                              justifyContent: "center",
                                              flexDirection: "column",
                                              gap: 6,
                                            }}
                                          >
                                            {selectedStatus ===
                                              ApplicationStatus.UPCOMING && (
                                              <Tooltip title="Chat with Influencer">
                                                <Button
                                                  style={{
                                                    display: isMobile
                                                      ? "none"
                                                      : "flex",
                                                    fontSize: 12,
                                                    height: 40,
                                                  }}
                                                  className="button-icon"
                                                  icon={
                                                    <MessagesIcon
                                                      style={{ width: 12 }}
                                                    />
                                                  }
                                                  onClick={() =>
                                                    onClickMessage(collab)
                                                  }
                                                >
                                                  Chat
                                                </Button>
                                              </Tooltip>
                                            )}
                                            {canRescheduleAccepted && (
                                              <RescheduleRequest
                                                influencer={collab}
                                                asButton
                                              />
                                            )}
                                          </div>
                                        </div>
                                        <div className="extra-info">
                                          <div
                                            style={{
                                              flex: 1,
                                              display: "flex",
                                              gap: 16,
                                              justifyContent: isMobile
                                                ? "center"
                                                : "flex-start",
                                            }}
                                          >
                                            <span className="text-strong-small flex-gap-4">
                                              <PurchaseIcon />{" "}
                                              {!isMobile && "Purchase Intent: "}
                                              {collab?.dinnerIntentByPosts ||
                                                "-"}
                                              /post
                                            </span>
                                            <span className="text-strong-small flex-gap-4">
                                              <FlashIcon />{" "}
                                              {!isMobile && "Engagement: "}{" "}
                                              {collab?.engagementRate || "-"}%
                                            </span>
                                            <span className="text-strong-small flex-gap-4">
                                              <StarIcon />{" "}
                                              {!isMobile && "Rating: "}{" "}
                                              {collab?.commitmentRate || "-"}%
                                            </span>
                                          </div>
                                        </div>
                                      </Col>
                                    );
                                  })}
                                </Row>
                              </Col>
                            </Row>
                          );
                        })}
                      </Col>
                    </Row>
                  </Col>
                </Row>
              );
            })
          ) : (
            <EmptySelectedTab />
          )}
        </Col>
      </Row>
    </div>
  );
};
export default Home;
